/**
 * ProductImageManager — ürün galeri görsel yönetimi (max 4).
 *
 * Bir ürünün görselleri iki kaynaktan gelir:
 *  - Kapak: Product.image  (POS/KDS tek görsel bekler)
 *  - Ek galeri: ProductImage kayıtları (max 3 → kapak ile toplam 4)
 *
 * Bu component yalnızca EK galeri görsellerini (ProductImage) yönetir.
 * Kapak görseli ürün formundaki `image` alanından düzenlenir.
 *
 * MinIO'ya orijinal yüklenir; backend Pillow ile thumbnail üretir
 * (image_thumbnail). Grid burada thumbnail'i gösterir — hızlı.
 *
 * Referans: ecom-prompts ProductMedia (sadeleştirilmiş — DnD/varyant yok).
 */
import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ActionIcon, Box, Group, Image, Loader, Overlay, Stack, Text, Tooltip,
} from "@mantine/core";
import {
  IconPhoto, IconStar, IconStarFilled, IconTrash, IconUpload, IconZoomScan,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useProductImages,
  useUploadProductImage,
  useDeleteProductImage,
} from "../../../shared/api";
import { useAction } from "../../../shared/api/resources";
import { Lightbox } from "../../../shared/ui";

// Kapak Product.image; ProductImage ek galeri → toplam 4 = kapak + 3.
const MAX_GALLERY = 3;
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

interface Props {
  productId: string | null;
  /** Kapak görseli URL'i (Product.image) — slot doluluğu bilgisi için. */
  coverImage?: string | null;
}

export function ProductImageManager({ productId, coverImage }: Props) {
  const { data, isLoading } = useProductImages(productId);
  const upload = useUploadProductImage();
  const remove = useDeleteProductImage();
  const setCover = useAction("product-images", "set-as-cover");
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  // Lightbox: açık görselin index'i (null = kapalı)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  if (!productId) {
    return (
      <Text size="sm" c="dimmed">
        Galeri görselleri için önce ürünü kaydedin.
      </Text>
    );
  }

  const images = (data?.results || data || []) as Array<{
    id: string;
    image: string;
    image_thumbnail: string | null;
  }>;
  // Toplam = kapak (varsa 1) + galeri. Üst sınır 4.
  const coverCount = coverImage ? 1 : 0;
  const usedSlots = coverCount + images.length;
  const canAddMore = images.length < MAX_GALLERY;

  // Lightbox sırası: kapak (varsa) önce, ardından galeri görselleri —
  // tam-boy `image` URL'i (thumbnail değil). Grid'deki sıralamayla eşleşir.
  const lightboxItems = [
    ...(coverImage ? [coverImage] : []),
    ...images.map((img) => img.image),
  ];

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || !productId) return;
    const remaining = MAX_GALLERY - images.length;
    const selected = Array.from(files).slice(0, remaining);
    if (Array.from(files).length > remaining) {
      notifications.show({
        message: `En fazla ${remaining} görsel daha eklenebilir`,
        color: "orange",
      });
    }
    for (const file of selected) {
      if (file.size > MAX_FILE_BYTES) {
        notifications.show({
          message: `${file.name} 5 MB sınırını aşıyor`,
          color: "red",
        });
        continue;
      }
      const fd = new FormData();
      fd.append("product", productId);
      fd.append("image", file);
      try {
        await upload.mutateAsync(fd);
      } catch (e: any) {
        notifications.show({
          message: e?.response?.data?.detail
            || e?.response?.data?.non_field_errors?.[0]
            || "Görsel yüklenemedi",
          color: "red",
        });
        break;
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  }

  async function handleRemove(id: string) {
    try {
      await remove.mutateAsync(id);
    } catch {
      notifications.show({ message: "Görsel silinemedi", color: "red" });
    }
  }

  async function handleSetCover(id: string) {
    try {
      await setCover.mutateAsync({ id });
      // useAction yalnız product-images'i invalidate eder; kapak Product.image'e
      // yazıldığı için ürün listesini de elle tazele.
      qc.invalidateQueries({ queryKey: ["products"] });
      notifications.show({ message: "Kapak görseli güncellendi", color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Kapak yapılamadı",
        color: "red",
      });
    }
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="sm" fw={600}>
          Ek Galeri Görselleri
        </Text>
        <Text size="xs" c="dimmed">
          {usedSlots} / 4 dolu (kapak dahil)
        </Text>
      </Group>

      {/* Görseller arası bol boşluk — köşedeki ⭐/🗑 butonları taşar,
          aralarındaki gap geniş tutulmazsa komşu kutuya değer. wrap ile
          dar modalda alt satıra iner. */}
      <Group gap={28} wrap="wrap" mt={6}>
        {/* Kapak — POS/QR menüde gösterilen ana görsel. Galeri görsellerindeki
            ⭐ butonu ile değiştirilir. */}
        {coverImage && (
          <Box
            pos="relative"
            w={112}
            h={112}
            style={{ cursor: "zoom-in" }}
            onClick={() => setLightboxIndex(0)}
          >
            <Image src={coverImage} w={112} h={112} radius="md" fit="cover" alt="Kapak" />
            <Group
              gap={3}
              pos="absolute"
              bottom={5}
              left={5}
              px={5}
              py={1}
              align="center"
              style={{ background: "rgba(0,0,0,0.65)", borderRadius: 4 }}
            >
              <IconStarFilled size={10} color="var(--mantine-color-yellow-4)" />
              <Text size="10px" fw={700} c="white">KAPAK</Text>
            </Group>
          </Box>
        )}

        {/* Galeri görselleri — tıklayınca lightbox açılır (kapaktan sonra
            geldikleri için index = coverCount + i). */}
        {images.map((img, i) => (
          <Box
            key={img.id}
            pos="relative"
            w={112}
            h={112}
            style={{ cursor: "zoom-in" }}
            onClick={() => setLightboxIndex(coverCount + i)}
          >
            <Image
              src={img.image_thumbnail || img.image}
              w={112}
              h={112}
              radius="md"
              fit="cover"
              alt="Galeri görseli"
            />
            {/* Büyüteç ipucu — hover'da görselin tıklanabilir olduğunu belli eder */}
            <Box
              pos="absolute"
              bottom={5}
              right={5}
              p={3}
              style={{ background: "rgba(0,0,0,0.55)", borderRadius: 4, lineHeight: 0 }}
            >
              <IconZoomScan size={13} color="white" />
            </Box>
            <ActionIcon
              color="red"
              variant="filled"
              size="md"
              radius="xl"
              pos="absolute"
              top={-8}
              right={-8}
              onClick={(e) => { e.stopPropagation(); handleRemove(img.id); }}
              loading={remove.isPending}
              aria-label="Görseli sil"
            >
              <IconTrash size={14} />
            </ActionIcon>
            {/* Kapak yap — bu görseli Product.image'e kopyalar */}
            <Tooltip label="Kapak görseli yap" withinPortal>
              <ActionIcon
                color="yellow"
                variant="filled"
                size="md"
                radius="xl"
                pos="absolute"
                top={-8}
                left={-8}
                onClick={(e) => { e.stopPropagation(); handleSetCover(img.id); }}
                loading={setCover.isPending}
                aria-label="Kapak yap"
              >
                <IconStar size={14} />
              </ActionIcon>
            </Tooltip>
          </Box>
        ))}

        {/* Yükleme kutusu */}
        {canAddMore && (
          <Box
            pos="relative"
            w={112}
            h={112}
            onClick={() => fileRef.current?.click()}
            style={{
              border: "2px dashed var(--mantine-color-gray-4)",
              borderRadius: "var(--mantine-radius-md)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {upload.isPending ? (
              <Loader size="sm" />
            ) : (
              <Stack gap={4} align="center">
                <IconUpload size={22} color="var(--mantine-color-gray-6)" />
                <Text size="10px" c="dimmed">Görsel Ekle</Text>
              </Stack>
            )}
            {upload.isPending && <Overlay opacity={0.1} />}
          </Box>
        )}

        {isLoading && <Loader size="sm" />}
      </Group>

      {!coverImage && images.length === 0 && !isLoading && (
        <Group gap={4}>
          <IconPhoto size={14} color="var(--mantine-color-gray-5)" />
          <Text size="xs" c="dimmed">Henüz görsel yok</Text>
        </Group>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        hidden
        onChange={(e) => handleFiles(e.target.files)}
      />

      {lightboxIndex !== null && (
        <Lightbox
          images={lightboxItems}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </Stack>
  );
}
